<template>
  <li v-if="shouldShow" class="nav-item" :class="{ 'dropdown': hasChildren, ...customClasses }">
    <!-- Link -->
    <component
      :is="linkComponent"
      :to="computedUrl"
      :href="computedUrl"
      :target="item.openInNewTab ? '_blank' : '_self'"
      :rel="item.openInNewTab ? 'noopener noreferrer' : ''"
      class="nav-link"
      :class="{ 'nav-submenu-toggle': hasChildren }"
      @click="hasChildren ? toggleDropdown($event) : null"
    >
      <i v-if="item.icon" :class="item.icon + ' me-1'"></i>
      {{ displayLabel }}
      <i v-if="hasChildren" class="bi bi-chevron-down ms-1"></i>
    </component>

    <!-- Dropdown Menu (Children) -->
    <div v-if="hasChildren" class="dropdown-menu" :class="{ show: dropdownOpen }">
      <nav-menu-item-child
        v-for="child in item.children"
        :key="child.id"
        :item="child"
        :is-logged-in="isLoggedIn"
        :is-admin="isAdmin"
        :menu-pages="menuPages"
        @close="closeDropdown"
      />
    </div>
  </li>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { MenuItemData } from '@/components/admin/MenuBuilder.vue'
import NavMenuItemChild from './NavMenuItemChild.vue'
import {
  resolveMenuItemLabel,
  resolveMenuItemUrl,
  type MenuLookupPage,
} from '@/utils/menuBuilderHelpers'

interface Props {
  item: MenuItemData
  isLoggedIn: boolean
  isAdmin: boolean
  menuPages?: MenuLookupPage[]
}

const props = withDefaults(defineProps<Props>(), {
  menuPages: () => [],
})

const dropdownOpen = ref(false)

const hasChildren = computed(() => {
  return props.item.children && props.item.children.length > 0
})

const shouldShow = computed(() => {
  if (!props.item.visible) return false
  
  switch (props.item.visibility) {
    case 'logged-in':
      return props.isLoggedIn
    case 'logged-out':
      return !props.isLoggedIn
    case 'admin':
      return props.isAdmin
    default:
      return true
  }
})

const linkComponent = computed(() => {
  // Use router-link for internal routes, <a> for external
  if (props.item.type === 'custom' && props.item.url?.startsWith('http')) {
    return 'a'
  }
  return 'router-link'
})

const computedUrl = computed(() => {
  return resolveMenuItemUrl(props.item, props.menuPages)
})

const displayLabel = computed(() => {
  return resolveMenuItemLabel(props.item, props.menuPages)
})

const customClasses = computed(() => {
  if (!props.item.cssClass) return {}
  return props.item.cssClass.split(' ').reduce((acc, className) => {
    acc[className] = true
    return acc
  }, {} as Record<string, boolean>)
})

function toggleDropdown(event: Event) {
  event.preventDefault()
  dropdownOpen.value = !dropdownOpen.value
}

function closeDropdown() {
  dropdownOpen.value = false
}
</script>

<style scoped>
.nav-item.dropdown {
  position: relative;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 200px;
  background: var(--nav-dropdown-bg, var(--nav-bg-color, #2c3e50));
  color: var(--nav-text-color, #ecf0f1);
  border: 1px solid var(--nav-dropdown-border, rgba(255, 255, 255, 0.15));
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  display: none;
  z-index: 1200;
  padding: 0.5rem 0;
  margin-top: 0;
}

.dropdown-menu.show {
  display: block;
}

.nav-link.nav-submenu-toggle {
  cursor: pointer;
}
</style>
